import type { ClientOrganization } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC, PropsWithChildren } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  ModalProps,
  NativeSyntheticEvent,
  StyleProp,
  ViewStyle
} from 'react-native';
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  View
} from 'react-native';
import { IconButton, Text } from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { EdgeInsets } from 'react-native-safe-area-context';

import { BackIcon } from '@/assets';
import { useOrganizationRegionName } from '@/features/profile-setting/hooks/useOrganizationRegionName';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { ExtendedModal } from '@/shared/components/ExtendedModal';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

type Props = Omit<ModalProps, 'children'> &
  PropsWithChildren<{
    /**
     * The organization currently being edited; used to resolve the region
     * name shown in the header title.
     */
    organization?: ClientOrganization;
    /**
     * Extra style merged into the ScrollView's contentContainerStyle. The
     * shell already provides the bottom safe-area padding and background —
     * callers use this to add gaps between sections, extra horizontal
     * padding, etc.
     */
    scrollContentContainerStyle?: StyleProp<ViewStyle>;
  }>;

/**
 * Shared chrome for the organization edit-page previews: full-screen modal
 * with a back-arrow + centered title header and a scrollable body slot.
 *
 * The header title reads "Vorschau: Darstellung in „<region>"" — the region
 * name is resolved from `organization.geoAreaIds[0]` via
 * useOrganizationRegionName, and falls back to a plain "Vorschau" when the
 * lookup is still pending or the org has no geo area.
 */
export const PreviewModalShell: FC<Props> = ({
  visible,
  onRequestClose,
  onDismiss,
  organization,
  scrollContentContainerStyle,
  children,
  ...modalProps
}) => {
  const theme = useAppTheme();
  const insets = useSafeAreaInsets();
  const styles = useMemo(() => makeStyles(theme, insets), [insets, theme]);
  const { t } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'screens.organization-edit.preview'
  });

  const { regionName } = useOrganizationRegionName(organization);

  const requestClose = () =>
    onRequestClose?.({} as NativeSyntheticEvent<never>);

  return (
    <ExtendedModal
      {...modalProps}
      visible={visible}
      onRequestClose={onRequestClose}
      onDismiss={onDismiss}
      statusBarTranslucent
      animationType="slide"
    >
      <KeyboardAvoidingView
        style={styles.wrapper}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <View style={styles.header}>
          <IconButton
            icon={({ size }) => (
              <BackIcon
                fill={theme.colors.onSurface}
                width={size}
                height={size}
              />
            )}
            size={24}
            onPress={requestClose}
            accessibilityLabel={t('close')}
            accessibilityHint={t('close')}
          />
          <Text
            variant="titleMedium"
            style={styles.headerTitle}
            numberOfLines={1}
          >
            {isEmptyOrNull(regionName)
              ? t('titleFallback')
              : t('title', { region: regionName })}
          </Text>
          <View style={styles.headerSpacer} />
        </View>

        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={[styles.scroll, scrollContentContainerStyle]}
        >
          {children}
        </ScrollView>
      </KeyboardAvoidingView>
    </ExtendedModal>
  );
};

const makeStyles = (theme: AppTheme, insets: EdgeInsets) =>
  StyleSheet.create({
    wrapper: {
      flex: 1,
      // White base so any gap in the ScrollView/layout chain still renders
      // white below the navigation header. The header re-applies
      // surfaceContainer for its own band.
      backgroundColor: theme.colors.surfaceContainerLowest,
      paddingTop: insets.top
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 8,
      paddingVertical: 4,
      backgroundColor: theme.colors.surfaceContainer
    },
    headerTitle: {
      flex: 1,
      textAlign: 'center',
      color: theme.colors.onSurface
    },
    headerSpacer: { width: 48 },
    // The background must live on the ScrollView itself (style), not on
    // contentContainerStyle: the content container only covers the content
    // area, so when the content is shorter than the screen the wrapper's
    // gray surfaceContainer would show through below it.
    scrollView: {
      flex: 1,
      backgroundColor: theme.colors.surfaceContainerLowest
    },
    scroll: {
      flexGrow: 1,
      paddingBottom: 32 + insets.bottom
    }
  });
